import React from 'react';
import RecraftGlyph from '../RecraftGlyph';
import { DamageSword } from './ResultPillIcons';
import CombatTooltip, { CombatTooltipPanel } from '../GameTip/CombatTooltip';
import { DiuresisPill } from '../StatusTags/KcStatusPills';
import { isHtnDrugTooltipRowsEnabled } from '../../utils/envFlags';
import {
  htnDrugInfluenceGroups,
  fedByDrugNames,
  diuresisFedRows,
} from './HtnDrugInfluenceTooltip';
import NephronDiagram, {
  activeNephronSegmentKeys,
  NEPHRON_SEGMENT_COUNT,
} from './NephronDiagram';
import { ShiftArrow } from './PlayerPotassiumTag';
import { isCombatTerminalPhase } from './playerbarPhase';

// Kidney glyph for the Nephron sub-tooltip head (design "Combat Hover Grammar").
const nephronIcon = (
  <svg
    className="combat-tt__node-icon"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    aria-hidden="true"
  >
    <path d="M15.5 3.2c-2.7 0-4.4 1.9-5 4.2c-.4 1.7-1.6 2.4-3 2.7C5.4 10.7 3.6 12.6 3.6 15.4c0 3.2 2.4 5.4 5.4 5.4c4.9 0 9.4-4.4 9.4-10.4c0-4.2-1.4-7.2-2.9-7.2z" />
  </svg>
);

// The diuresis card's accent, in ONE place. The nephron figure has no colour of its
// own: on hover it inherits this from the CombatTooltip host it opens inside, so a
// second mount with no host would silently fall back to the panel's default amber and
// show the same figure in a different colour on two surfaces. Retyping the literal at
// the second call site is the same drift by hand, so it is named here instead.
export const HTN_DIURESIS_ACCENT = 'rgb(45, 212, 191)';

/**
 * THE Nephron card. Extracted verbatim from htnDiuresisTooltipProps 2026-08-20 so a
 * SECOND mount can exist without a second definition: the combat prelude renders the
 * same card in flow (it is already `standalone`, so nothing about it is hover-shaped)
 * to put the figure on screen for a room that will never hover.
 *
 * Its "N Of 5 Active" subtitle and the lit segments both derive from the SAME
 * activeNephronSegmentKeys source, so the count and the figure can never disagree —
 * and now cannot disagree BETWEEN the two mounts either.
 */
export function htnNephronPanel({ activeEquippedEffects = [] }) {
  const activeSegments = activeNephronSegmentKeys(activeEquippedEffects).size;
  return (
    <CombatTooltipPanel
      standalone
      name="Nephron"
      subtitle={`${activeSegments} Of ${NEPHRON_SEGMENT_COUNT} Active`}
      icon={nephronIcon}
      className="combat-tt__panel--nephron"
      sections={[{ content: <NephronDiagram activeEquippedEffects={activeEquippedEffects} /> }]}
    />
  );
}

// HTN Regimen "Diuresis" drain (flat model, 2026-07-25). Each equipped diuretic
// applies a FLAT per-round drain to EVERY enemy, set by the diuretic's clinical
// potency (loop >> thiazide > K-sparing). A 2nd agent at an already-covered segment
// adds nothing at all, and each additional distinct nephron segment adds a flat bonus.
// It does NOT accumulate — the drain is constant for the loadout. The exact scalar
// is computed in the engine (`diuresis_flat_per_enemy`) and shipped as
// `diuresis.impending`; this chip renders that `drain` value verbatim.
//
// Edward 2026-07-21: Diuresis is a debuff ON THE ENEMY (it drains each enemy), so
// this renders in the focused-enemy status rail (BatchBossSession), NOT the player
// rail — unlike serum potassium / pulse, which are player/body states. Hence the
// drug-agnostic name (no "Player" prefix).
/**
 * THE diuresis tooltip. Returns CombatTooltip props (everything except children) so
 * the enemy rail and the drug toast hover the identical panel from one definition
 * rather than each reconstructing it (Edward 2026-07-25: the toast's embedded pill
 * had no tooltip at all while the rail hovered this).
 */
export function htnDiuresisTooltipProps({
  drain = 0,
  segments = 0,
  activeEquippedEffects = [],
  // ENEMY-RAIL ONLY (Edward 2026-07-25): the Nephron sub-tooltip attaches to the
  // RIGHT of the diuresis hover ONLY when this tag lives in the focused-enemy status
  // rail. Passed true solely from BatchBossSession's enemy-rail render; any other
  // mount (including the drug toast) gets the diuresis card alone.
  inEnemyRail = false,
}) {
  const drainValue = Math.max(0, Math.round(Number(drain) || 0));
  const segLine = Number(segments) >= 2
    ? `${segments} nephron segments covered: each extra segment adds a flat bonus on top.`
    : 'Cover a different nephron segment. A second drug on one segment adds no drain at all.';
  const damageLine = `Drains ${drainValue} HP from each enemy every round (flat, set by diuretic potency).`;
  // The on-rail chip wears the shared authored droplet pill (kc-pill--diuresis) so
  // it reads on the SAME visual key as the player-rail HTN meters (HR / K+ pulse):
  // identical kc-pill chassis — teal droplet glyph + colored left-stripe. The rich
  // drug-agnostic tooltip + "Fed by" footnote below stay on the CombatTooltip wrapper.
  // The base rail chip reads just "Diuresis" — no number (Edward 2026-07-24); the
  // live per-enemy drain stays in the hover detail.
  if (!isHtnDrugTooltipRowsEnabled()) {
    return {
      inline: true,
      color: 'rgb(56, 189, 248)',
      name: 'Diuresis',
      placement: 'below-left',
      icon: <RecraftGlyph slug="diuresis" size={18} className="combat-tt__node-icon" />,
      sections: [{ lines: [damageLine, segLine] }],
    };
  }

  // Drug-agnostic status tooltip in the potassium-grade STAT grammar (Edward's
  // 2026-07-24 port-up to match the Claude Design "Player status tooltips" page).
  // Compact, full-width: the Diuresis chip row carries the flat per-enemy drain on
  // the RIGHT — "Drains ⚔ N each round" (teal, because it damages enemies) — then a
  // "Fed by" per-drug segment ledger (every diuretic feeds it, so all arrows up).
  // The split header states the segment coverage (name │ N Segments). Accent teal.
  const diuresisGroups = htnDrugInfluenceGroups(activeEquippedEffects, 'diuresis');
  const fedRows = diuresisFedRows(diuresisGroups);
  const distinctSegments = Math.max(0, Math.trunc(Number(segments) || 0));
  const subtitle = distinctSegments >= 1
    ? `${distinctSegments} Segment${distinctSegments === 1 ? '' : 's'}`
    : 'Draining';
  const diuresisBody = (
    <span className="gt-inflicts-rows gt-diuresis-rows">
      <span className="gt-inflicts">
        <span className="gt-inflicts__l">
          <DiuresisPill label="Diuresis" />
        </span>
        <span className="gt-inflicts__r">
          <span className="gt-statrow">
            <span className="gt-stat gt-stat--join"><span className="gt-stat__v">Drains</span></span>
            <span className="gt-stat gt-stat--drain">
              <DamageSword />
              <span className="gt-stat__v">{drainValue}</span>
            </span>
            <span className="gt-stat gt-stat--join"><span className="gt-stat__v">each enemy / round</span></span>
          </span>
        </span>
      </span>
      {fedRows.length > 0 && (
        <>
          <span className="gt-inflicts gt-inflicts--shift">
            <span className="gt-inflicts__l"><span className="gt-text">Fed by</span></span>
            <span className="gt-inflicts__r" />
          </span>
          <span className="gt-shift-ledger">
            {fedRows.map((rowItem) => (
              <span className="gt-shift-row" key={rowItem.slug}>
                <span className={`gt-shift-dir gt-shift-dir--${rowItem.direction}`}>
                  <ShiftArrow direction={rowItem.direction} />
                </span>
                <span className="gt-shift-name">{rowItem.drugName}</span>
                {rowItem.classLabel ? (
                  <span className="gt-shift-class">{rowItem.classLabel}</span>
                ) : null}
              </span>
            ))}
          </span>
        </>
      )}
    </span>
  );
  const fedBy = fedByDrugNames(diuresisGroups);
  const ariaLabel = [
    `Diuresis · ${subtitle}`,
    `Drains ${drainValue} HP from each enemy each round`,
    fedBy.length ? `Fed by ${fedBy.join(', ')}` : '',
  ].filter(Boolean).join('. ');
  // Nephron sub-tooltip — a standalone card that reveals to the RIGHT of the
  // diuresis hover, ONLY in the enemy rail. Built by the shared htnNephronPanel
  // above, which the combat prelude's mechanism band also mounts.
  const nephronPanel = inEnemyRail ? htnNephronPanel({ activeEquippedEffects }) : null;
  return {
    color: HTN_DIURESIS_ACCENT,
    name: 'Diuresis',
    subtitle,
    placement: 'below-left',
    icon: <RecraftGlyph slug="diuresis" size={18} className="combat-tt__node-icon" />,
    sections: [{ content: diuresisBody }],
    ariaLabel,
    secondaryPanel: nephronPanel,
  };
}

function DiuresisTag({
  drain = 0,
  segments = 0,
  activeEquippedEffects = [],
  visible = false,
  phase,
  inEnemyRail = false,
}) {
  const drainValue = Math.max(0, Math.round(Number(drain) || 0));
  if (!visible || isCombatTerminalPhase(phase) || !(drainValue > 0)) {
    return null;
  }
  return (
    <CombatTooltip
      {...htnDiuresisTooltipProps({ drain: drainValue, segments, activeEquippedEffects, inEnemyRail })}
    >
      <DiuresisPill label="Diuresis" />
    </CombatTooltip>
  );
}

export default DiuresisTag;
